import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { availableSpotsKeys, getAllSpotsByCondominium } from '../api/available-spots';
import { getMyParkingSpots, getMySpotUsages, myParkingSpotsKeys } from '../api/my-parking-spots';
import { parkingListQueryOptions } from '../lib/parking-query';
import { useCurrentCondominium } from './useCurrentCondominium';

function filterSpotsByCondominium<T extends { spotId: string; condominiumId?: string }>(
  items: T[],
  condominiumId: string,
  spotIdsInCondominium: Set<string>,
): T[] {
  if (!condominiumId) return [];

  return items.filter((item) => {
    if (item.condominiumId) {
      return item.condominiumId === condominiumId;
    }
    return spotIdsInCondominium.has(item.spotId);
  });
}

export function useTenantParkingSpots() {
  const { condominium, label, isLoading: isCondominiumLoading } = useCurrentCondominium();
  const condominiumId = condominium?.id ?? '';

  const condoSpotsQuery = useQuery({
    queryKey: availableSpotsKeys.allSpots(condominiumId),
    queryFn: () => getAllSpotsByCondominium(condominiumId),
    enabled: !!condominiumId,
    ...parkingListQueryOptions,
  });

  const spotIdsInCondominium = useMemo(
    () => new Set((condoSpotsQuery.data ?? []).map((spot) => spot.id)),
    [condoSpotsQuery.data],
  );

  const spotsQuery = useQuery({
    queryKey: myParkingSpotsKeys.list(condominiumId),
    queryFn: getMyParkingSpots,
    enabled: !!condominiumId,
    ...parkingListQueryOptions,
    select: (spots) => filterSpotsByCondominium(spots, condominiumId, spotIdsInCondominium),
  });

  const borrowedQuery = useQuery({
    queryKey: myParkingSpotsKeys.borrowed(condominiumId),
    queryFn: getMySpotUsages,
    enabled: !!condominiumId && condoSpotsQuery.isSuccess,
    ...parkingListQueryOptions,
    select: (usages) => {
      const active = usages.filter((usage) => usage.status === 'ACTIVE');
      const ownedSpotIds = new Set((spotsQuery.data ?? []).map((spot) => spot.spotId));
      return active.filter(
        (usage) => spotIdsInCondominium.has(usage.spotId) && !ownedSpotIds.has(usage.spotId),
      );
    },
  });

  return {
    condominium,
    condominiumId,
    condominiumLabel: label,
    spots: spotsQuery.data ?? [],
    borrowedUsages: borrowedQuery.data ?? [],
    isLoading:
      isCondominiumLoading ||
      spotsQuery.isLoading ||
      condoSpotsQuery.isLoading ||
      borrowedQuery.isLoading,
    isError: spotsQuery.isError || condoSpotsQuery.isError,
    refetch: async () => {
      await Promise.all([spotsQuery.refetch(), condoSpotsQuery.refetch(), borrowedQuery.refetch()]);
    },
  };
}
